/**
 * FirmaKullaniciEkleModal.jsx — Firma Detay › Portal Kullanıcıları › "Kullanıcı ekle" (2026-10-19)
 *
 * Var olan bir firma tarafı girişi firmaya bağlar (user_to_firma): kullanıcı aranır, satırdaki düğme PUT /users/{id}/firma çağırır.
 * #628 (2026-10-30): uçlar artık MANAGE_USER_ROLES ya da kullanici.olustur ister (Kayıt ve Saha rolleri); arama GET
 * /users/firma-kullanici-adaylari, YALNIZCA firma tarafı girişleri döner (personel sunulmaz). Bir kullanıcının TEK firması olur
 * (başka firmaya bağlıysa buraya taşınır). Yeni giriş OLUŞTURMAK için yanındaki "Yeni kullanıcı oluştur" düğmesi var. Bağlanan giriş
 * listede varsayılan rolüyle görünür; rolü sonra "Rolleri düzenle" ile verilir.
 * WAS (until #628): rbac.manage ister (Yetki Yönetimi'nin uçları: GET /rbac/users, PUT /rbac/users/{id}/firma), yalnızca kök geçerdi.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
// WAS (until #628): import { listManagedUsers, setUserFirma } from "../../../../services/rbacService";
import { listFirmaKullaniciAdaylari, setKullaniciFirma } from "../../../../services/userService";

const adi = (u) => [u.userName, u.userSurname].filter(Boolean).join(" ") || u.userLoginName || `#${u.userId}`;

/**
 * @param {number}   firmaId
 * @param {string}   firmaAdi
 * @param {Function} toggle     pencereyi kapatır
 * @param {Function} onEklendi  (kullaniciAdi) => void — bağlama başarılı olunca; sayfa listeyi yeniden okur
 */
export default function FirmaKullaniciEkleModal({ firmaId, firmaAdi, toggle, onEklendi }) {
  const { t } = useTranslation("ayarlar");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [adaylar, setAdaylar] = useState([]);
  const [araniyor, setAraniyor] = useState(false);
  const [busy, setBusy] = useState(null);
  const [hata, setHata] = useState(null);

  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (query.length < 2) return undefined;
    let ignore = false;
    async function run() {
      setAraniyor(true);
      try {
        // WAS (until #628): const out = await listManagedUsers({ q: query, size: 10 }); ... setAdaylar(out?.users || []);
        const out = await listFirmaKullaniciAdaylari({ q: query, size: 10 });
        if (!ignore) { setAdaylar(out || []); setHata(null); }
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setAraniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [query]);

  async function ekle(u) {
    setBusy(u.userId);
    setHata(null);
    try {
      // WAS (until #628): await setUserFirma(u.userId, firmaId);
      await setKullaniciFirma(u.userId, firmaId);
      onEklendi(adi(u));
    } catch (e) {
      setHata(e.message);
      setBusy(null);
    }
  }

  const gorunen = query.length >= 2 ? adaylar : [];

  return (
    <Modal isOpen toggle={busy === null ? toggle : undefined} size="lg">
      <ModalHeader toggle={busy === null ? toggle : undefined}>{t("portalKullanicilari.ekleBaslik", { firma: firmaAdi || `#${firmaId}` })}</ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2" toggle={() => setHata(null)} data-testid="kullanici-ekle-hata">{hata}</Alert>}
        <Label for="fke-ara" className="form-label small mb-1">{t("portalKullanicilari.kullaniciAra")}</Label>
        <Input
          id="fke-ara" bsSize="sm" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("portalKullanicilari.aramaYeri")} data-testid="kullanici-ekle-ara"
        />
        {query.length < 2 && <div className="form-text">{t("portalKullanicilari.aramaIpucu")}</div>}
        {araniyor && <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>}
        {!araniyor && query.length >= 2 && gorunen.length === 0 && (
          <div className="text-muted small py-3 text-center">{t("portalKullanicilari.sonucYok")}</div>
        )}
        {gorunen.length > 0 && (
          <div className="table-responsive mt-2">
            <Table size="sm" className="align-middle mb-0" data-testid="kullanici-ekle-sonuc">
              <tbody>
                {gorunen.map((u) => {
                  const bagli = (u.firmalar || []).some((f) => f.firmaId === firmaId);
                  const baska = (u.firmalar || []).filter((f) => f.firmaId !== firmaId);
                  return (
                    <tr key={u.userId} data-kullanici-id={u.userId}>
                      <td>
                        <div className="fw-semibold">{adi(u)}</div>
                        <div className="small text-muted font-monospace">{u.userLoginName} · #{u.userId}</div>
                      </td>
                      <td className="small">{u.userGroupName || "—"}</td>
                      <td className="small text-muted">{baska.map((f) => f.firmaName || `#${f.firmaId}`).join(", ")}</td>
                      <td className="text-end text-nowrap">
                        {bagli ? (
                          <span className="text-muted small">{t("portalKullanicilari.zatenBagli")}</span>
                        ) : (
                          <Button
                            size="sm" color="primary" disabled={busy !== null} data-testid={`kullanici-ekle-${u.userId}`}
                            title={baska.length ? t("portalKullanicilari.tasiIpucu") : undefined}
                            onClick={() => ekle(u)}
                          >
                            {busy === u.userId ? <Spinner size="sm" /> : (baska.length ? t("portalKullanicilari.tasi") : t("portalKullanicilari.ekle"))}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
        <div className="form-text mt-3">{t("portalKullanicilari.tekFirmaNotu")}</div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={busy !== null}>{t("portalKullanicilari.kapat")}</Button>
      </ModalFooter>
    </Modal>
  );
}
